import { rem } from 'polished'
import React, { useCallback } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useNavigateTo from 'hooks/useNavigateTo'
import Notes from 'tripPlanner/components/CuratedTrip/Notes'
import Section from 'tripPlanner/components/CuratedTrip/Section'
import ItemCard from 'tripPlanner/components/ItemCard/ItemCard'
import TripSummaryCardLoadingSkeleton from 'tripPlanner/components/TripSummary/TripSummaryCardLoadingSkeleton'
import { useCurator } from 'tripPlanner/hooks/api'
import useCuratedTripSummaryData from 'tripPlanner/hooks/useCuratedTripSummaryData'
import { useTripIncludesFlashOffer } from 'tripPlanner/hooks/useTripIncludesFlashOffer'
import { ContinuePlanningBanner } from 'tripPlanner/pages/curatedTrips/ContinuePlanningBanner'
import TripPlannerFilters from 'tripPlanner/pages/trip/TripPlannerFilters'
import CreatorProfile from 'tripPlanner/Tripkit/CuratedTrips/CreatorProfile'
import { FullTrip } from 'tripPlanner/types/common'

const Summary = styled.div`
  padding: ${rem(16)} 0;
`

const TopSection = styled.div`
  padding: ${rem(16)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const FlashOfferWarning = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(20)};
  background: ${(props) => props.theme.palette.messaging.warning.light};
`

const WarningIcon = styled(LineInfoCircleIcon)`
  margin-top: 2px;
`

interface Props extends RouteComponentProps {
  trip: FullTrip
}

function TripSummary({ trip }: Props) {
  const {
    isLoading,
    isFetched,
    summarySections,
    itemCount,
    filteredTripSummary,
    availableSubFilters,
    filters,
  } = useCuratedTripSummaryData({ trip })

  const includesFlashOffer = useTripIncludesFlashOffer(trip)

  const { data: curator } = useCurator({ tripId: trip.id })

  const navigateTo = useNavigateTo()
  const onClickCard = useCallback(
    (offerLink: string) => {
      navigateTo(offerLink)
    },
    [navigateTo],
  )

  if (isLoading && !isFetched) {
    return (
      <Summary>
        <TripSummaryCardLoadingSkeleton />
        <TripSummaryCardLoadingSkeleton />
        <TripSummaryCardLoadingSkeleton height={238} />
        <TripSummaryCardLoadingSkeleton height={192} />
      </Summary>
    )
  }

  return (
    <Summary>
      <TopSection>
        {curator?.showBio && curator?.name && (
          <CreatorProfile
            name={curator.name}
            subheading={curator.position}
            imgId={curator.imageId}
            size="large"
          />
        )}
        {trip.description && <Notes content={trip.description} />}
        {includesFlashOffer && (
          <FlashOfferWarning>
            <WarningIcon size="XS" />
            <BodyText variant="medium">
              There are{' '}
              <BodyText variant="medium" weight="bold" as="span">
                Limited Time Lux Exclusive
              </BodyText>{' '}
              offers in this trip. Be quick before they end or sell out!
            </BodyText>
          </FlashOfferWarning>
        )}
        {filteredTripSummary && (
          <TripPlannerFilters
            selectedFilters={filters}
            availableSubFilters={availableSubFilters}
            itemCount={itemCount}
          />
        )}
        {filters.length > 0 &&
          filteredTripSummary?.entries.map((s) => {
            return match(s)
              .with({ type: 'ITEM', item: P.select() }, (item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  variant="tripSummary"
                  onClickCard={onClickCard}
                />
              ))
              .otherwise(() => null)
          })}
      </TopSection>
      {filters.length == 0 &&
        summarySections.map((section, i) => (
          <Section key={i} section={section} onClickCard={onClickCard} />
        ))}
      <ContinuePlanningBanner trip={trip} />
    </Summary>
  )
}

export default withRouter(TripSummary)
